/**
 * ErrorBoundary —— landing 精简版
 *
 * 砍掉了模板里的 `fallback` prop 自定义渲染（landing 全站用默认 UI 就够）。
 * 保留 `onError` callback —— 等接 Sentry 时直接传 captureError。
 *
 * 完整版（带自定义 fallback / 详细文档）在：
 *   /Users/yinan/Documents/compo-templates/runtime-l2-template/ErrorBoundary.tsx
 *
 * 注意：React boundaries 只抓 render-time 错误，不抓：
 *   - async rejection（用 window.onunhandledrejection 补）
 *   - event handler 抛错（用 try/catch 补）
 *   - SSR 错误（要 framework-specific 机制）
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface State {
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.props.onError?.(error, info);
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div role="alert" className="container" style={{ padding: '80px 24px', textAlign: 'center' }}>
        <h1 style={{ fontSize: 24, marginBottom: 12 }}>Something went wrong</h1>
        <p style={{ color: 'var(--color-muted)', marginBottom: 24 }}>
          {error.message || 'An unexpected error occurred.'}
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <button onClick={this.reset} className="btn-ghost">Try again</button>
          <button onClick={() => location.reload()} className="btn-primary">Refresh</button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
